import UTIF from "utif";
import QRCode from "qrcode";
import { rgb } from "pdf-lib";
import { svg2png, initialize } from "svg2png-wasm";

// Initialize svg2png-wasm once
let svg2pngInitialized = false;
const initializeSvg2Png = async () => {
  if (!svg2pngInitialized) {
    await initialize(fetch("https://unpkg.com/svg2png-wasm/svg2png_wasm_bg.wasm"));
    svg2pngInitialized = true;
  }
};

/**
 * Generate QR code as data URL
 */
export const generateQR = async (text) => {
  try {
    return await QRCode.toDataURL(text);
  } catch (err) {
    return null;
  }
};

/** Rounded-rectangle path (clockwise from top-left). */
const pathRoundedRect = (ctx, x, y, w, h, r) => {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + rad, y);
  ctx.lineTo(x + w - rad, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rad);
  ctx.lineTo(x + w, y + h - rad);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rad, y + h);
  ctx.lineTo(x + rad, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rad);
  ctx.lineTo(x, y + rad);
  ctx.quadraticCurveTo(x, y, x + rad, y);
  ctx.closePath();
};

/**
 * QR code as PNG data URL with rounded corners (quiet zone stays white).
 * Used for PDF header where a softer tile shape reads better than a sharp square.
 */
export const generateRoundedQRDataUrl = async (text, opts = {}) => {
  try {
    const pixelSize = opts.pixelSize ?? 256;
    const borderRadius = opts.borderRadius ?? Math.round(pixelSize * 0.1);

    const canvas = document.createElement("canvas");
    canvas.width = pixelSize;
    canvas.height = pixelSize;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return await QRCode.toDataURL(text);
    }

    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    pathRoundedRect(ctx, 0, 0, pixelSize, pixelSize, borderRadius);
    ctx.fill();

    ctx.save();
    ctx.beginPath();
    pathRoundedRect(ctx, 0, 0, pixelSize, pixelSize, borderRadius);
    ctx.clip();

    const qrCanvas = document.createElement("canvas");
    await QRCode.toCanvas(qrCanvas, text, {
      margin: 1,
      width: pixelSize,
      color: { dark: "#000000", light: "#ffffff" },
    });
    ctx.drawImage(qrCanvas, 0, 0, pixelSize, pixelSize);
    ctx.restore();

    return canvas.toDataURL("image/png");
  } catch (err) {
    return generateQR(text);
  }
};

/**
 * Convert hex color to RGB for pdf-lib
 */
export const hexToRgb = (hex) => {
  const cleanHex = hex.replace(/^#/, "");
  const bigint = parseInt(cleanHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return rgb(r / 255, g / 255, b / 255);
};

/**
 * Embed image from URL into PDF document
 */
export const embedImageFromUrl = async (pdfDoc, imageUrl) => {
  try {
    const imageResponse = await fetch(imageUrl);
    const contentType = imageResponse.headers.get("content-type");

    let embeddedImage;

    // Handle SVG files
    if (contentType?.includes("svg") || imageUrl.toLowerCase().endsWith(".svg")) {
      try {
        // Initialize svg2png-wasm if not already done
        await initializeSvg2Png();

        // Get SVG text content
        const svgText = await imageResponse.text();

        // Convert SVG to PNG
        const pngData = await svg2png(svgText, {
          scale: 2, // Higher quality
          width: 400,
          height: 200, // Default height, can be adjusted
        });

        // Embed PNG in PDF
        embeddedImage = await pdfDoc.embedPng(pngData);
      } catch (svgError) {
        console.error("Error converting SVG:", svgError);
        return null;
      }
    }
    // Handle TIFF files
    else if (
      contentType?.includes("tiff") ||
      contentType?.includes("tif") ||
      imageUrl.toLowerCase().endsWith(".tiff") ||
      imageUrl.toLowerCase().endsWith(".tif")
    ) {
      try {
        // Get TIFF as ArrayBuffer
        const tiffBuffer = await imageResponse.arrayBuffer();

        // Decode TIFF
        const ifds = UTIF.decode(tiffBuffer);
        UTIF.decodeImage(tiffBuffer, ifds[0]);

        // Convert to RGBA
        const rgba = UTIF.toRGBA8(ifds[0]);

        // Create canvas to convert RGBA to PNG
        const canvas = document.createElement('canvas');
        canvas.width = ifds[0].width;
        canvas.height = ifds[0].height;
        const ctx = canvas.getContext('2d');

        // Create ImageData from RGBA buffer
        const imageData = ctx.createImageData(canvas.width, canvas.height);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);

        // Convert canvas to PNG blob
        const pngBlob = await new Promise((resolve) => {
          canvas.toBlob(resolve, 'image/png');
        });

        // Convert blob to ArrayBuffer
        const pngArrayBuffer = await pngBlob.arrayBuffer();

        // Embed PNG in PDF
        embeddedImage = await pdfDoc.embedPng(pngArrayBuffer);
      } catch (tiffError) {
        console.error("Error converting TIFF:", tiffError);
        return null;
      }
    }
    else {
      // Handle PNG/JPG files
      const imageBytes = await imageResponse.arrayBuffer();

      if (contentType?.includes("png")) {
        embeddedImage = await pdfDoc.embedPng(imageBytes);
      } else if (contentType?.includes("jpeg") || contentType?.includes("jpg")) {
        embeddedImage = await pdfDoc.embedJpg(imageBytes);
      } else {
        // Try PNG first, then JPG as fallback
        try {
          embeddedImage = await pdfDoc.embedPng(imageBytes);
        } catch {
          embeddedImage = await pdfDoc.embedJpg(imageBytes);
        }
      }
    }

    return embeddedImage;
  } catch (error) {
    console.error("Error embedding image:", error);
    return null;
  }
};

/**
 * Create PDF blob and return URL
 */
export const createPdfBlobUrl = async (pdfDoc) => {
  const pdfBytes = await pdfDoc.save();
  const file = new Blob([pdfBytes], { type: "application/pdf" });
  const fileURL = URL.createObjectURL(file);
  return { fileURL, pdfBytes };
};
